<template>
  <div class="container-fluid p-4">
    <div class="flex items-center justify-between review-surge-header">
      <div class="min-w-0 flex-1 mr-4">
        <h3 class="h3 my-0 mx-0 text-foreground">Review Surge</h3>
        <p class="text-muted-foreground text-sm mt-1 review-surge-subtitle">
          Bulk review request campaigns. Upload contacts here and we’ll drip out requests automatically based on your series (this replaces the old Bulk Request tab).
        </p>
      </div>
      <router-link
        v-if="user && user.business"
        :to="{
          name: 'request-reviews',
          params: {
            /* eslint-disable-next-line vue/camelcase */
            unique_id: user.business.unique_id + '-' + user.business.id,
          },
        }"
        class="btn bg-primary space-x-3 round-xl px-4 request-review-btn request-review-top-cta review-surge-request-btn"
        target="_blank"
        ><i class="uil uil-star"></i
        ><span class="text-sm font-medium"> Request Review</span>
      </router-link>
    </div>
    <hr class="pb-3 pt-3" />
    <!-- Upload Section -->
      <div class="upload-card">
      <div class="upload-card-header">
        <div>
          <h4 class="upload-card-title">Bulk Review Request Upload</h4>
          <p class="upload-card-subtitle">
            Upload a CSV or Excel file that contains the contacts you’d like to invite. We’ll queue them and honor your daily send limit for Review Surge.
          </p>
        </div>
        <button class="btn btn-outline-secondary btn-sm round-xl px-4 download-csv-btn" @click="downloadSampleCSV">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-2 h-4 w-4"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" x2="12" y1="15" y2="3"></line>
          </svg>
          Download Sample CSV
        </button>
      </div>
      <div class="upload-card-body">
        <ValidationObserver ref="bulkUploadForm" v-slot="{ handleSubmit, invalid, touched }">
          <form class="upload-form" @submit.prevent="handleSubmit(submitFile)">
            <div class="upload-grid">
              <div class="form-field">
                <label class="form-label" for="campaign-name">Campaign Name</label>
                <input
                  id="campaign-name"
                  v-model="campaignName"
                  type="text"
                  class="form-input"
                  placeholder="Spring Cleaning Review Push"
                />
              </div>
              <div class="form-field">
                <label class="form-label" for="daily-limit">Max Daily Send Limit</label>
                <input
                  id="daily-limit"
                  v-model.number="uploadLimit"
                  type="number"
                  class="form-input"
                  min="1"
                  max="1000"
                  placeholder="100"
                />
                <p class="form-help-text">Number of review request emails to send each day.</p>
              </div>
              <div class="form-field">
                <label class="form-label" for="employee">Employee</label>
                <select id="employee" v-model="employee_id" class="form-input form-select">
                  <option :value="null">Unknown</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.name }}
                  </option>
                </select>
                <p class="form-help-text">
                  Applied to every contact unless the CSV row sets <code>employee_email</code>.
                </p>
              </div>
            </div>
            <div
              class="file-drop-area"
              :class="{ 'is-dragover': dragActive }"
              @dragover.prevent="dragActive = true"
              @dragleave.prevent="dragActive = false"
              @drop.prevent="handleFileDrop"
            >
              <input
                id="file-upload"
                ref="fileInput"
                type="file"
                name="bulk"
                class="form-input file-input"
                accept=".csv,.xlsx,.xls"
                @change="setFile"
              />
              <p class="file-drop-title">
                Drag &amp; drop your CSV or Excel file here, or <span>browse</span>
              </p>
              <p class="file-drop-subtitle">
                Accepted file types: .csv, .xlsx, .xls • First row should contain headers.
                Optional column: <code>employee_email</code> (overrides the Employee selected above).
              </p>
              <p v-if="fileUpload" class="file-selected">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
                {{ fileUpload.name }}
              </p>
            </div>
            <div class="upload-actions">
              <button
                type="submit"
                class="upload-button"
                :disabled="loadingUpdate || !fileUpload || (invalid && touched)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="upload-icon"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" x2="12" y1="3" y2="15"></line>
                </svg>
                <span v-if="!loadingUpdate">Upload Campaign</span>
                <span v-else>Uploading...</span>
              </button>
              <p class="upload-hint">
                We’ll queue contacts immediately. You can monitor campaign progress below once the upload finishes.
              </p>
            </div>
            <div v-if="results.length" class="results-section">
              <strong class="results-title">Upload log</strong>
              <ul class="results-list">
                <li v-for="(row, i) in results" :key="i">{{ row }}</li>
              </ul>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
    <div class="space-y-6">
      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 stats-grid">
        <div class="stat-card">
          <div class="stat-card-header">
            <h3 class="stat-card-title">Active Campaigns</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="stat-icon"
            >
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <div class="stat-card-body">
            <div class="stat-value">{{ activeCampaignsCount }}</div>
            <p class="stat-description">+{{ activeCampaignsCount - 1 }} from last month</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-header">
            <h3 class="stat-card-title">Total Contacts</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="stat-icon"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div class="stat-card-body">
            <div class="stat-value">{{ totalContactsFormatted }}</div>
            <p class="stat-description">Across all campaigns</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-header">
            <h3 class="stat-card-title">Emails Sent</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="stat-icon"
            >
              <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
              <path d="m21.854 2.147-10.94 10.939"></path>
            </svg>
          </div>
          <div class="stat-card-body">
            <div class="stat-value">{{ emailsSentFormatted }}</div>
            <p class="stat-description">{{ completionRate }}% completion rate</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-header">
            <h3 class="stat-card-title">Click Rate</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="stat-icon"
            >
              <path d="M12.586 12.586 19 19"></path>
              <path d="M3.688 3.037a.497.497 0 0 0-.651.651l6.5 15.999a.501.501 0 0 0 .947-.062l1.569-6.083a2 2 0 0 1 1.448-1.479l6.124-1.579a.5.5 0 0 0 .063-.947z"></path>
            </svg>
          </div>
          <div class="stat-card-body">
            <div class="stat-value">{{ clickRate }}%</div>
            <p class="stat-description text-success">+5.2% vs industry avg</p>
          </div>
        </div>
      </div>

      <!-- Campaign Overview Table -->
      <div class="campaign-overview-card">
        <div class="campaign-overview-header">
          <h3 class="campaign-overview-title">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mr-2 h-5 w-5"
            >
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M8 13h2"></path>
              <path d="M14 13h2"></path>
              <path d="M8 17h2"></path>
              <path d="M14 17h2"></path>
            </svg>
            Campaign Overview
          </h3>
        </div>
            <div class="campaign-overview-body">
          <div v-if="retrievingTableData" class="text-center py-4">
            <b-spinner small></b-spinner>
            <span class="ml-2">Loading campaigns...</span>
          </div>
          <div v-else class="table-container">
            <table class="campaign-table">
              <thead>
                <tr>
                  <th>Campaign Name</th>
                  <th>Upload Date</th>
                  <th>Contacts</th>
                  <th>Progress</th>
                  <th>Daily Limit</th>
                  <th>Clicks</th>
                  <th>Issues</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="campaign in campaigns" :key="campaign.id">
                  <td class="font-medium">{{ campaign.name || 'Unnamed Campaign' }}</td>
                  <td>
                    <div class="flex items-center text-sm text-muted-foreground">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-1 h-3 w-3"
                      >
                        <path d="M8 2v4"></path>
                        <path d="M16 2v4"></path>
                        <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                        <path d="M3 10h18"></path>
                      </svg>
                      {{ campaign.uploadDate }}
                    </div>
                  </td>
                  <td>
                    <div class="text-sm">
                      <div class="font-medium">{{ campaign.totalContacts }} total</div>
                      <div class="text-muted-foreground">{{ campaign.sentContacts }} sent</div>
                    </div>
                  </td>
                  <td>
                    <div class="progress-wrapper">
                      <div class="progress-bar-container">
                        <div
                          class="progress-bar-fill"
                          :style="{ width: campaign.progress + '%' }"
                        ></div>
                      </div>
                      <div class="text-sm text-muted-foreground">{{ campaign.progress }}%</div>
                    </div>
                  </td>
                  <td>
                    <div class="flex items-center text-sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-1 h-3 w-3"
                      >
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      {{ campaign.dailyLimit }}/day
                    </div>
                  </td>
                  <td>
                    <div class="text-sm font-medium text-success">{{ campaign.clicks }} clicks</div>
                  </td>
                  <td>
                    <div class="text-sm">
                      <div v-if="campaign.bounces > 0" class="text-destructive">{{ campaign.bounces }} bounces</div>
                      <div v-if="campaign.spam > 0" class="text-destructive">{{ campaign.spam }} spam</div>
                      <div v-if="campaign.bounces === 0 && campaign.spam === 0" class="text-success">No issues</div>
                    </div>
                  </td>
                  <td>
                    <div
                      class="status-badge"
                      :class="{
                        'status-active': campaign.status === 'active',
                        'status-completed': campaign.status === 'completed',
                        'status-pending': campaign.status === 'pending',
                      }"
                    >
                      {{ campaign.status }}
                    </div>
                  </td>
                  <td>
                    <button
                      class="action-btn"
                      @click="viewCampaignDetails(campaign)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="h-4 w-4"
                      >
                        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                  </td>
                </tr>
                <tr v-if="campaigns.length === 0">
                  <td colspan="9" class="text-center py-4 text-muted-foreground">
                    No campaigns found. Create your first campaign above.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="!retrievingTableData && campaigns.length > 0" class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>Total {{ totalContacts }}</span>
            <div class="flex items-center space-x-2">
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="requestContactsDatatable.queries.page === 1"
                @click="requestContactsDatatable.queries.page > 1 && (requestContactsDatatable.queries.page--, onReviewRequestLogPageChanged(requestContactsDatatable.queries.page))"
              >
                «
              </button>
              <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
                {{ requestContactsDatatable.queries.page }}
              </div>
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="requestContactsDatatable.queries.page >= Math.ceil(totalContacts / requestContactsDatatable.queries.per_page)"
                @click="requestContactsDatatable.queries.page < Math.ceil(totalContacts / requestContactsDatatable.queries.per_page) && (requestContactsDatatable.queries.page++, onReviewRequestLogPageChanged(requestContactsDatatable.queries.page))"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New Campaign Modal -->
    <div v-if="showNewCampaignModal" class="modal-overlay" @click.self="showNewCampaignModal = false">
      <div role="dialog" class="new-campaign-modal" @click.stop>
        <ValidationObserver ref="bulkUpdateForm" v-slot="{ handleSubmit, invalid, touched }">
          <form class="modal-content" @submit.prevent="handleSubmit(submitFile)">
            <header class="modal-header">
              <h2 class="modal-title">Upload New Campaign</h2>
              <p class="modal-subtitle">
                Upload a CSV or Excel file, set a daily limit, and Review Surge will drip requests automatically.
              </p>
            </header>
            <div class="modal-body">
              <div class="form-field">
                <label class="form-label" for="modal-campaign-name">Campaign Name</label>
                <input
                  id="modal-campaign-name"
                  v-model="campaignName"
                  type="text"
                  class="form-input"
                  placeholder="Holiday reminders"
                />
              </div>
              <div class="form-field">
                <label class="form-label" for="modal-daily-limit">Max Daily Send Limit</label>
                <input
                  id="modal-daily-limit"
                  v-model.number="uploadLimit"
                  type="number"
                  class="form-input"
                  min="1"
                  max="1000"
                  placeholder="100"
                />
                <p class="form-help-text">How many review requests should go out each day?</p>
              </div>
              <div class="form-field">
                <label class="form-label" for="modal-employee">Employee</label>
                <select id="modal-employee" v-model="employee_id" class="form-input form-select">
                  <option :value="null">Unknown</option>
                  <option v-for="employee in employees" :key="'modal-' + employee.id" :value="employee.id">
                    {{ employee.name }}
                  </option>
                </select>
                <p class="form-help-text">
                  Applied to every contact unless the CSV row sets <code>employee_email</code>.
                </p>
              </div>
              <div class="form-field">
                <label class="form-label">CSV / Excel File</label>
                <div
                  class="file-drop-area"
                  :class="{ 'is-dragover': dragActive }"
                  @dragover.prevent="dragActive = true"
                  @dragleave.prevent="dragActive = false"
                  @drop.prevent="handleFileDrop"
                >
                  <input
                    id="modal-file-upload"
                    ref="fileInput"
                    type="file"
                    name="bulk"
                    class="form-input file-input"
                    accept=".csv,.xlsx,.xls"
                    @change="setFile"
                  />
                  <p class="file-drop-title">
                    Drag &amp; drop your file here, or <span>browse</span>
                  </p>
                  <p class="file-drop-subtitle">
                    Accepted file types: .csv, .xlsx, .xls • Make sure the first row contains headers.
                    Optional column: <code>employee_email</code>.
                  </p>
                  <p v-if="fileUpload" class="file-selected">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    {{ fileUpload.name }}
                  </p>
                </div>
              </div>
            </div>
            <footer class="modal-footer">
              <button type="button" class="btn btn-light" @click="showNewCampaignModal = false">Cancel</button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="loadingUpdate || !fileUpload || (invalid && touched)"
              >
                <b-spinner v-if="loadingUpdate" small class="mr-1" />
                <span v-else>Upload Campaign</span>
              </button>
            </footer>
            <div v-if="results.length" class="results-section">
              <strong class="results-title">Upload log</strong>
              <ul class="results-list">
                <li v-for="(row, i) in results" :key="i">{{ row }}</li>
              </ul>
            </div>
          </form>
        </ValidationObserver>
        <button type="button" class="modal-close-button" @click="showNewCampaignModal = false">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="close-icon">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span class="sr-only">Close</span>
        </button>
      </div>
    </div>

    <!-- Campaign Details Modal -->
    <b-modal
      v-model="showCampaignDetailsModal"
      :title="selectedCampaign ? selectedCampaign.name : 'Campaign Details'"
      :hide-footer="true"
      size="lg"
    >
      <div v-if="selectedCampaign">
        <table class="table table-bordered">
          <tr>
            <td>Series</td>
            <td>
              {{ selectedCampaign.review_request_series
                ? selectedCampaign.review_request_series.map((o) => o.name).join(', ')
                : 'N/A' }}
            </td>
          </tr>
          <tr>
            <td>Next Run Time</td>
            <td>
              <span v-if="selectedCampaign.finished">Finished</span>
              <span
                v-else-if="selectedCampaign.next_series_process_time_front_date !== null"
              >
                {{ selectedCampaign.next_series_process_time_front_date }}
              </span>
              <span v-else>Pending</span>
            </td>
          </tr>
          <tr>
            <td colspan="2" class="text-center bg-primary text-white">Visits</td>
          </tr>
          <tr>
            <td colspan="2" class="p-0">
              <table
                v-if="selectedCampaign.visits && selectedCampaign.visits.length"
                class="table w-100 table-striped"
              >
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Step</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="visit in selectedCampaign.visits" :key="visit.id">
                    <td>{{ visit.type }}</td>
                    <td>{{ visit.step }}</td>
                    <td>{{ visit.front_date }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-center p-2">None</div>
            </td>
          </tr>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import '../../../design/custom/components/_dashboard.scss'
import { ValidationObserver } from 'vee-validate'
import axios from 'axios'

export default {
  components: {
    ValidationObserver,
  },
  data() {
    return {
      user: null,
      loadingUpdate: false,
      fileUpload: null,
      uploadLimit: 100,
      campaignName: '',
      employee_id: null,
      results: [],
      retrievingTableData: false,
      loadingStats: false,
      showCampaignDetailsModal: false,
      selectedCampaign: null,
      dragActive: false,
      // Statistics from API
      stats: {
        total_contacts: 0,
        sent_contacts: 0,
        finished_contacts: 0,
        total_clicks: 0,
        click_rate: 0,
        completion_rate: 0,
        active_campaigns: 0,
      },
      requestContactsDatatable: {
        items: [],
        columns: [
          { key: 'front_date', label: 'Created', sortable: true },
          { key: 'first_name', label: 'First Name', sortable: true },
          { key: 'last_name', label: 'Last Name', sortable: true },
          { key: 'email', label: 'Email', sortable: true },
          { key: 'cell_phone', label: 'Cell Phone', sortable: true },
          { key: 'total_steps', label: 'Total Steps' },
          { key: 'current_step', label: 'Current Step' },
          { key: 'finished', label: 'Finished' },
          { key: 'id', label: '' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          search: null,
          sort_by: 'created_at',
          sort_desc: false,
          is_bulk: true,
        },
      },
    }
  },
  computed: {
    csvDownloadLink() {
      return (
        (process.env.NODE_ENV === 'production'
          ? process.env.VUE_APP_BASE_API
          : process.env.VUE_APP_BASE_API_LOCAL) + 'bulk-contact-request-sample'
      )
    },
    reviewContacts() {
      return this.$store.getters['contactRequests/all'] || []
    },
    totalContacts() {
      return this.$store.getters['contactRequests/total'] || 0
    },
    campaigns() {
      // Group contacts by campaign name
      const campaignMap = {}
      this.reviewContacts.forEach((contact) => {
        const campaignName = contact.name || 'Unnamed Campaign'
        if (!campaignMap[campaignName]) {
          campaignMap[campaignName] = {
            id: contact.id,
            name: campaignName,
            contacts: [],
            uploadDate: contact.front_date || contact.created_at || 'N/A',
            dailyLimit: contact.upload_limit || 50,
            review_request_series: contact.review_request_series || [],
            finished: contact.finished || false,
            deleted_at: contact.deleted_at,
            next_series_process_time_front_date:
              contact.next_series_process_time_front_date || null,
            visits: contact.visits || [],
          }
        }
        campaignMap[campaignName].contacts.push(contact)
      })

      // Calculate statistics for each campaign
      return Object.values(campaignMap).map((campaign) => {
        const totalContacts = campaign.contacts.length
        const sentContacts = campaign.contacts.filter((c) => c.current_step > 0).length
        const finishedContacts = campaign.contacts.filter((c) => c.finished).length
        const progress = totalContacts > 0 ? Math.round((sentContacts / totalContacts) * 100) : 0

        // Calculate clicks (assuming visits with type 'click')
        const clicks = campaign.contacts.reduce((sum, c) => {
          if (c.visits) {
            return sum + c.visits.filter((v) => v.type === 'click').length
          }
          return sum
        }, 0)

        // Calculate bounces and spam (placeholder - adjust based on actual data structure)
        const bounces = campaign.contacts.filter((c) => c.bounced).length || 0
        const spam = campaign.contacts.filter((c) => c.spam).length || 0

        // Determine status
        let status = 'pending'
        if (campaign.finished || campaign.deleted_at) {
          status = 'completed'
        } else if (sentContacts > 0) {
          status = 'active'
        }

        return {
          ...campaign,
          totalContacts,
          sentContacts,
          finishedContacts,
          progress,
          clicks,
          bounces,
          spam,
          status,
        }
      })
    },
    activeCampaignsCount() {
      return this.stats.active_campaigns || 0
    },
    totalContactsFormatted() {
      return (this.stats.total_contacts || 0).toLocaleString()
    },
    emailsSentFormatted() {
      return (this.stats.sent_contacts || 0).toLocaleString()
    },
    completionRate() {
      return this.stats.completion_rate || 0
    },
    clickRate() {
      const rate = this.stats.click_rate || 0
      return typeof rate === 'number' ? rate.toFixed(1) : rate
    },
    businessId() {
      return this.user?.business?.id
    },
    employees() {
      return this.$store.getters['employees/all'] || []
    },
  },
  mounted() {
    if (!this.user) {
      this.user = Vue.util.extend({}, this.$store.getters['auth/user'])
    }
    if (this.businessId) {
      this.fetchContactRequestStats()
    }
    this.getEmployees()
    this.getContacts()
    this.showNewCampaignModal = false
  },
  methods: {
    getEmployees() {
      this.$store.dispatch('employees/getAll', {
        queries: {
          per_page: 500,
          page: 1,
          sort_by: 'name',
          sort_desc: false,
        },
        page: 'index',
      })
    },
    async fetchContactRequestStats() {
      if (!this.businessId) return

      this.loadingStats = true
      try {
        const response = await axios.get(`/businesses/${this.businessId}/contact-request-stats`)
        const data = response.data.data

        if (response.data.success && data) {
          this.stats = {
            total_contacts: data.total_contacts || 0,
            sent_contacts: data.sent_contacts || 0,
            finished_contacts: data.finished_contacts || 0,
            total_clicks: data.total_clicks || 0,
            click_rate: data.click_rate || 0,
            completion_rate: data.completion_rate || 0,
            active_campaigns: data.active_campaigns || 0,
          }
        }
      } catch (error) {
        console.error('Error fetching contact request stats:', error)
        if (Vue.$toast) {
          Vue.$toast.open({
            message: 'Failed to load contact request statistics. Please try again.',
            type: 'error',
            duration: 5000,
          })
        }
      } finally {
        this.loadingStats = false
      }
    },
    getContacts() {
      if (!this.user || !this.user.business) return

      this.retrievingTableData = true
      this.$store
        .dispatch('contactRequests/getAll', {
          queries: this.requestContactsDatatable.queries,
          page: 'contacts',
          businessId: this.user.business.id,
        })
        .then(() => {
          this.retrievingTableData = false
          // Refresh stats after fetching contacts
          this.fetchContactRequestStats()
        })
        .catch((err) => {
          this.retrievingTableData = false
          console.error(err)
        })
    },
    onReviewRequestLogPageChanged(page) {
      this.requestContactsDatatable.queries.page = page
      this.getContacts()
    },
    setFile(event) {
      this.fileUpload = event.target.files[0]
    },
    handleFileDrop(event) {
      this.dragActive = false
      if (event && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
        this.fileUpload = event.dataTransfer.files[0]
      }
    },
    submitFile() {
      if (!this.fileUpload) {
        Vue.$toast.open({
          message: 'Please select a CSV or Excel file first.',
          type: 'error',
          duration: 5000,
        })
        return
      }

      this.loadingUpdate = true
      this.dragActive = false

      const uploadForm = new FormData()
      uploadForm.append('csv', this.fileUpload)
      if (this.uploadLimit) {
        uploadForm.append('uploadLimit', this.uploadLimit)
      }
      if (this.campaignName) {
        uploadForm.append('campaign_name', this.campaignName)
      }
      if (this.employee_id) {
        uploadForm.append('employee_id', this.employee_id)
      }
      uploadForm.append('business_id', this.user.business.id)

      this.$store
        .dispatch('business/bulkAddReviews', uploadForm)
        .then((resp) => {
          this.loadingUpdate = false
          this.fileUpload = null
          this.campaignName = ''
          this.employee_id = null
          this.uploadLimit = 100
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
          this.results = resp.data || []
          this.showNewCampaignModal = false

          if (resp.total > 0) {
            Vue.$toast.open({
              message: 'Campaign uploaded successfully',
              type: 'success',
              duration: 5000,
            })
            this.getContacts()
          }
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.errors) {
            if (this.$refs.bulkUpdateForm) {
              this.$refs.bulkUpdateForm.setErrors(err.response.data.errors)
            }
          }
          this.loadingUpdate = false
          Vue.$toast.open({
            message: err.response?.data?.message || 'Error uploading campaign',
            type: 'error',
            duration: 5000,
          })
        })
    },
    downloadSampleCSV() {
      window.open(this.csvDownloadLink, '_blank')
    },
    viewCampaignDetails(campaign) {
      // Find the full contact data for this campaign
      const fullCampaign = this.reviewContacts.find((c) => c.name === campaign.name) || campaign
      this.selectedCampaign = {
        ...campaign,
        ...fullCampaign,
      }
      this.showCampaignDetailsModal = true
    },
  },
}
</script>

<style lang="scss" scoped>
.container-fluid {
  min-height: 100vh;
  background: white;
}

.review-surge-header {
  align-items: flex-start;
  gap: 1rem;

  .min-w-0 {
    min-width: 0;
  }

  .review-surge-subtitle {
    margin-top: 0.25rem;
    max-width: 640px;
  }

  .review-surge-request-btn {
    flex-shrink: 0;
    white-space: nowrap;
    min-height: 40px;
  }
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.space-x-4 {
  > * + * {
    margin-left: 1rem;
  }
}

.space-x-6 {
  > * + * {
    margin-top: 1.5rem;
  }
}

.grid {
  display: grid;
  gap: 1.5rem;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

// Upload card + bulk upload layout
.upload-card {
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 1.5rem 1.75rem 1.75rem;
  margin-bottom: 2rem;
}

.upload-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
}

.upload-card-title {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
}

.upload-card-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #4b5563;
  max-width: 640px;
}

.upload-form {
  margin-top: 1.5rem;
}

.upload-grid {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.file-drop-area {
  margin-top: 0.5rem;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px dashed #d1d5db;
  background-color: #f3f4f6;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.file-drop-area.is-dragover {
  background-color: #e0ebff;
  border-color: #3b82f6;
}

.file-drop-title {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;

  span {
    color: #2563eb;
    font-weight: 600;
  }
}

.file-drop-subtitle {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.file-selected {
  margin-top: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #065f46;
}

.upload-actions {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.upload-hint {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}

@media (min-width: 768px) {
  .md\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}


// Statistics Cards
.stat-card {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.stat-card-header {
  padding: 1.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
}

.stat-card-title {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.025em;
  margin: 0;
}

.stat-icon {
  width: 1rem;
  height: 1rem;
  color: #6b7280;
}

.stat-card-body {
  padding: 1.5rem;
  padding-top: 0;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stat-description {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.text-success {
  color: #10b981;
}

// Campaign Overview Card
.campaign-overview-card {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.campaign-overview-header {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.campaign-overview-title {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  margin: 0;
}

.campaign-overview-body {
  padding: 1.5rem;
  padding-top: 0;
}

.table-container {
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  overflow: auto;
}

.campaign-table {
  width: 100%;
  font-size: 0.875rem;
  border-collapse: collapse;

  thead {
    tr {
      border-bottom: 1px solid #e5e7eb;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e5e7eb;
      transition: background-color 0.2s;

      &:last-child {
        border-bottom: 0;
      }

      &:hover {
        background-color: #f9fafb;
      }
    }
  }

  th {
    height: 3rem;
    padding: 0 1rem;
    text-align: left;
    vertical-align: middle;
    font-weight: 500;
    color: #6b7280;
  }

  td {
    padding: 1rem;
    vertical-align: middle;

    &[role='checkbox'] {
      padding-right: 0;
    }
  }
}

.font-medium {
  font-weight: 500;
}

.text-sm {
  font-size: 0.875rem;
}

.text-muted-foreground {
  color: #6b7280;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.h-3 {
  height: 0.75rem;
}

.w-3 {
  width: 0.75rem;
}

.h-4 {
  height: 1rem;
}

.w-4 {
  width: 1rem;
}

.h-5 {
  height: 1.25rem;
}

.w-5 {
  width: 1.25rem;
}

// Progress Bar
.progress-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.progress-bar-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 9999px;
  background-color: #e5e7eb;
  height: 0.5rem;
}

.progress-bar-fill {
  height: 100%;
  width: 0%;
  flex: 1 1 0%;
  background-color: #3b82f6;
  transition: all 0.3s;
}

// Status Badge
.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid transparent;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
  text-transform: capitalize;
}

.status-active {
  background-color: #10b981;
  color: white;
  border-color: transparent;

  &:hover {
    background-color: #059669;
  }
}

.status-completed {
  background-color: #3b82f6;
  color: white;
  border-color: transparent;

  &:hover {
    background-color: #2563eb;
  }
}

.status-pending {
  background-color: #f3f4f6;
  color: #6b7280;
  border-color: transparent;

  &:hover {
    background-color: #e5e7eb;
  }
}

.text-destructive {
  color: #ef4444;
}

// Action Button
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.25rem;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    color: #374151;
  }
}

// Pagination
.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

// Buttons
.download-csv-btn,
.new-campaign-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.5rem;
  padding: 0 1rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
}

.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.max-upload {
  max-width: 100px;
}

// New Campaign Modal
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.new-campaign-modal {
  position: relative;
  display: grid;
  width: 100%;
  max-width: 32rem;
  gap: 1rem;
  border: none;
  background: white;
  padding: 1.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;

  @media (min-width: 640px) {
    max-width: 28rem;
    border-radius: 0.5rem;
  }
}

.modal-header {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  text-align: center;

  @media (min-width: 640px) {
    text-align: left;
  }
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: none;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
}

.form-input {
  display: flex;
  height: 2.5rem;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background: white;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: #111827;
  transition: all 0.2s;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    border-color: #3b82f6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
}

.form-select {
  appearance: auto;
  cursor: pointer;
}

.file-input {
  cursor: pointer;

  &::file-selector-button {
    border: 0;
    background: transparent;
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
    padding: 0;
    margin-right: 0.5rem;
  }
}

.form-help-text {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.5rem;
  padding: 0 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}

.upload-icon {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

.results-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.results-title {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.results-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 0.25rem 0;
    color: #374151;
  }
}

.modal-close-button {
  position: absolute;
  right: 1rem;
  top: 1rem;
  border-radius: 0.125rem;
  opacity: 0.7;
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
    background-color: #f3f4f6;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
}

.close-icon {
  width: 1rem;
  height: 1rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>

